import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import {
  CreditCard,
  Landmark,
  Smartphone,
  Mail,
  PlusCircle,
  X,
  Eye,
  EyeOff,
  Award,
  Gift,
  Users,
  Coins,
  Activity,
  BarChart3,
  History,
  ChevronRight,
  Wallet2,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// NOTE ON PATHS — this file assumes it lives at the same depth
// as the original PaymentMethods / Payouts / Awards components.
// Adjust the relative "../redux/..." and asset paths below
// to match wherever you drop this file in the project tree.
// ─────────────────────────────────────────────────────────────
import type { AppDispatch, RootState } from "../redux/store";
import { listenToPaymentMethods, CardInfo } from "../redux/paymentMethodSlice";
import {
  fetchPayoutMethods as fetchPayoutMethodsList,
  upsertPayoutMethod as upsertPayoutMethodGeneric,
} from "../redux/payoutMethodSlice";
import { listenToHostPayout } from "../redux/payoutsSlice";
import {
  fetchPayoutMethods as fetchPayoutDetails,
  upsertPayoutMethod,
} from "../redux/payoutDetailsSlice";
import countries from "../GLOBAL/Countries";
import appLogo from "../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   BACKEND ENDPOINTS — everything the page talks to lives here.
   To hide a payout method from the UI entirely, comment out
   its line below AND its matching entry in PAYOUT_TABS further
   down. Nothing else needs to change.
═══════════════════════════════════════════════════════════ */
const BACKEND = {
  stripeConnect: "https://aloglam.web.app/createHostConnectAccount", //{stripe}
  wiseRecipient: "https://europe-west3-alreluxe.cloudfunctions.net/createWiseRecipient", //{wise}
  skrillRecipient: "https://YOUR_BACKEND_HERE/createSkrillRecipient", //{skrill} — replace once the Skrill function is deployed
  // paypalPayoutBackend: "https://YOUR_BACKEND_HERE/createPaypalRecipient", //{paypal} — uncomment if/when you add a dedicated PayPal backend call
};

// Route the "Analytics" / "Transactions" rows in the Payouts tab navigate to.
const ROUTES = {
  walletAnalytics: "/walletanalytics",
  walletTransactions: "/wallettransactions",
};

/* ═══════════════════════════════════════════════════════════
   PEEKABEBI DESIGN TOKENS — same brand as CategoryDetails:
   logo orange→red gradient over warm baby cream/peach.
═══════════════════════════════════════════════════════════ */
const F = {
  pageBg:   "#FFF8F1",
  cardBg:   "#FFFFFF",
  headerBg: "#FF6A2A",

  ink:      "#2B1A12",
  muted:    "#9C8A7E",
  mutedDim: "#B79C87",

  orange:   "#FF7A1A",
  red:      "#FF3D57",
  cream:    "#FFF3E6",
  peach:    "#FFD9BE",
  peachDim: "rgba(255,122,26,0.10)",

  border:   "rgba(43,26,18,0.10)",

  grad:     "linear-gradient(135deg,#FF9A3D 0%,#FF3D57 100%)",
  gradH:    "linear-gradient(90deg,#FF9A3D 0%,#FF3D57 100%)",

  outline:  "1.5px solid rgba(43,26,18,0.10)",

  emerald:  "#2E9B6B",
  emeraldSoft: "rgba(46,155,107,0.12)",
  rose:     "#E23B4E",
  roseSoft: "rgba(226,59,78,0.10)",
};

const SIDEBAR_W = 220;
const HEADER_H = 64;
const FOOTER_H = 64;

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;}
  .wl-root{font-family:'Montserrat',sans-serif;position:relative;}
  .wl-root ::-webkit-scrollbar{display:none;}
  .wl-root input, .wl-root select, .wl-root button{font-family:inherit;}
  .wl-root :focus-visible{outline:2px solid ${F.red};outline-offset:2px;}

  @keyframes wl-rise{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
  @keyframes wl-gradspin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes wl-pop{0%{transform:scale(.9);opacity:0}100%{transform:scale(1);opacity:1}}

  .wl-rise{animation:wl-rise .45s ease both;}

  .wl-field{position:relative;margin-bottom:16px;}
  .wl-field input, .wl-field select{
    width:100%;background:${F.cream};border:1.5px solid rgba(255,122,26,.16);
    color:${F.ink};border-radius:12px;padding:13px 14px;font-size:13.5px;
    outline:none;transition:border-color .15s,box-shadow .15s;
  }
  .wl-field input::placeholder{color:${F.mutedDim};}
  .wl-field input:focus, .wl-field select:focus{border-color:${F.red};box-shadow:0 0 0 3px rgba(255,61,87,.12);}
  .wl-field input.wl-err, .wl-field select.wl-err{border-color:${F.rose};}
  .wl-field input:disabled, .wl-field select:disabled{opacity:.6;cursor:not-allowed;}
  .wl-field label{
    display:block;font-size:10px;letter-spacing:.14em;text-transform:uppercase;
    color:${F.mutedDim};margin-bottom:7px;font-weight:800;
  }
  .wl-errtext{color:${F.rose};font-size:11.5px;margin-top:5px;font-weight:600;}

  .wl-tab{
    display:flex;align-items:center;gap:7px;padding:10px 16px;border-radius:999px;
    cursor:pointer;color:#FFE9D9;font-size:12.5px;font-weight:700;transition:all .16s;
    border:1.5px solid rgba(255,255,255,.28);background:rgba(255,255,255,.14);white-space:nowrap;flex-shrink:0;
  }
  .wl-tab:hover{background:rgba(255,255,255,.24);}
  .wl-tab.active{color:${F.red};background:#fff;border-color:#fff;}

  .wl-side-tab{
    display:flex;align-items:center;gap:10px;padding:12px 14px;border-radius:14px;
    cursor:pointer;color:${F.muted};font-size:13px;font-weight:700;transition:all .15s;width:100%;
    border:1.5px solid transparent;background:transparent;
  }
  .wl-side-tab:hover{background:${F.peachDim};color:${F.ink};}
  .wl-side-tab.active{color:#fff;background:${F.gradH};box-shadow:0 6px 16px rgba(255,61,87,.25);}

  .wl-foot-tab{
    display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;
    flex:1;color:${F.mutedDim};font-size:10.5px;font-weight:700;cursor:pointer;transition:color .15s;
  }
  .wl-foot-tab.active{color:${F.red};}

  .wl-pill{
    padding:9px 16px;border-radius:999px;font-size:12px;font-weight:700;cursor:pointer;
    border:1.5px solid rgba(255,122,26,.22);color:${F.muted};background:#fff;transition:all .15s;
  }
  .wl-pill.active{color:#fff;background:${F.gradH};border-color:transparent;}

  .wl-btn{
    width:100%;padding:14px 0;border-radius:14px;border:none;cursor:pointer;
    font-weight:800;font-size:13.5px;letter-spacing:.02em;transition:transform .12s,opacity .12s,box-shadow .12s;
  }
  .wl-btn:active{transform:scale(.98);}
  .wl-btn:disabled{cursor:not-allowed;opacity:.45;}

  .wl-card{transition:box-shadow .2s,transform .2s;}
  .wl-card:hover{box-shadow:0 10px 28px rgba(255,122,26,.14);transform:translateY(-2px);}
  .wl-card.wl-clickable{cursor:pointer;}

  .wl-amount{
    display:inline-flex;align-items:center;font-family:'Montserrat',sans-serif;
    font-weight:900;letter-spacing:-0.3px;
    background:${F.gradH};background-size:200% 100%;animation:wl-gradspin 3s ease infinite;
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;
  }

  .wl-seal{
    display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:6px 13px;
    font-size:11px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;
  }

  .wl-watermark{
    position:fixed;inset:0;display:flex;align-items:center;justify-content:center;
    pointer-events:none;z-index:0;overflow:hidden;
  }
  .wl-watermark img{width:60vw;max-width:640px;opacity:0.04;filter:grayscale(1);}

  @media (prefers-reduced-motion: reduce){ .wl-rise{animation:none;} }
`;

/* ═══════════════════════════════════════════════════════════
   helpers
═══════════════════════════════════════════════════════════ */
const formatCompact = (num: number = 0): string => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toString();
};
const maskLast4 = (value?: string) => {
  if (!value) return "—";
  return "•••• " + value.replace(/\s+/g, "").slice(-4);
};
const titleCase = (s: string) => (s ?? "unknown").replace(/_/g, " ").toUpperCase();

/* ═══════════════════════════════════════════════════════════
   SEAL — confirmation chip that pops in after a save/apply
═══════════════════════════════════════════════════════════ */
const Seal: React.FC<{ show: boolean; label?: string }> = ({ show, label = "Saved" }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, scale: 0.7, y: -4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85 }}
        transition={{ type: "spring", stiffness: 300, damping: 18 }}
        className="wl-seal"
        style={{ color: F.emerald, background: F.emeraldSoft, border: `1.5px solid ${F.emerald}` }}
      >
        <Sparkles size={13} /> {label}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ═══════════════════════════════════════════════════════════
   Field — shared styled input
═══════════════════════════════════════════════════════════ */
const Field: React.FC<{
  label: string; name: string; value: string; placeholder?: string;
  error?: string; onChange: (name: string, value: string) => void;
  disabled?: boolean; as?: "input" | "select"; children?: React.ReactNode;
}> = ({ label, name, value, placeholder, error, onChange, disabled, as = "input", children }) => (
  <div className="wl-field">
    <label htmlFor={name}>{label}</label>
    {as === "select" ? (
      <select id={name} name={name} value={value} disabled={disabled}
        className={error ? "wl-err" : ""} onChange={(e) => onChange(name, e.target.value)}>
        {children}
      </select>
    ) : (
      <input id={name} name={name} value={value} placeholder={placeholder} disabled={disabled}
        className={error ? "wl-err" : ""} onChange={(e) => onChange(name, e.target.value)} />
    )}
    {error && <div className="wl-errtext">{error}</div>}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
type TabKey = "wallet" | "payouts" | "rewards";

// To hide the Rewards tab entirely, comment out its entry below.
const NAV_TABS: { key: TabKey; label: string; icon: React.ReactElement }[] = [
  { key: "wallet", label: "Wallet", icon: <CreditCard size={16} /> },
  { key: "payouts", label: "Payouts", icon: <Landmark size={16} /> },
  { key: "rewards", label: "Rewards", icon: <Award size={16} /> }, //{rewards} — comment out to hide the tab
];

const Wallet: React.FC = () => {
  const [tab, setTab] = useState<TabKey>("wallet");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 900);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const user = useSelector((s: RootState) => s.auth.user);
  const userId = user?.userId;

  return (
    <div className="wl-root" style={S.page}>
      <style>{GLOBAL_CSS}</style>

      {/* watermark */}
      <div className="wl-watermark">
        <img src={appLogo} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
      </div>

      {/* ── fixed top header (desktop + mobile) ── */}
      <div style={S.fixedHeader}>
        <div style={S.brandRow}>
          <div style={S.logoRing}>
            <img src={appLogo} alt="" style={S.logoImg} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
          </div>
          <div>
            <div style={S.eyebrowLight}>YOUR MONEY</div>
            <h1 style={S.title}>WALLET</h1>
          </div>
        </div>
      </div>

      {/* ── desktop sidebar ── */}
      {!isMobile && (
        <div style={S.sidebar}>
          <div style={S.sidebarSubhead}>
            How you pay, how you get paid, and what you've earned — all in one place.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 18 }}>
            {NAV_TABS.map((t) => (
              <div key={t.key} className={`wl-side-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
                {t.icon}<span>{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── content ── */}
      <div style={{ ...S.content, marginLeft: isMobile ? 0 : SIDEBAR_W, paddingBottom: isMobile ? FOOTER_H + 24 : 40 }}>
        {isMobile && (
          <p style={S.mobileSubhead}>How you pay, how you get paid, and what you've earned — all in one place.</p>
        )}
        {tab === "wallet" && <WalletPanel userId={userId} />}
        {tab === "payouts" && <PayoutsPanel userId={userId} />}
        {tab === "rewards" && <RewardsPanel userId={userId} />}
      </div>

      {/* ── mobile bottom tab bar ── */}
      {isMobile && (
        <div style={S.footerBar}>
          {NAV_TABS.map((t) => (
            <div key={t.key} className={`wl-foot-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
              {t.icon}
              <span>{t.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Wallet;

/* ═══════════════════════════════════════════════════════════
   WALLET — payment methods used to pay (cards on file)
═══════════════════════════════════════════════════════════ */
const WalletPanel: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { cards } = useSelector((s: RootState) => s.paymentMethods);
  const [expanded, setExpanded] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ cardNumber: "", expMonth: "", expYear: "", cvv: "", zip: "", country: "" });

  useEffect(() => { if (userId) dispatch(listenToPaymentMethods(userId)); }, [userId, dispatch]);

  const onChange = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = () => {
    // { integrate with Stripe / your card-vaulting endpoint here }
    console.log("Submitting card:", form);
    setSaved(true);
    setExpanded(false);
    setForm({ cardNumber: "", expMonth: "", expYear: "", cvv: "", zip: "", country: "" });
    setTimeout(() => setSaved(false), 2400);
  };

  return (
    <div className="wl-rise">
      <PanelHeading eyebrow="Payment methods" title="Cards on file" action={<Seal show={saved} label="Card added" />} />

      <div style={S.stack}>
        {cards?.length > 0 ? (
          cards.map((card: CardInfo, i: number) => (
            <motion.div key={i} className="wl-card" style={S.rowCard} whileHover={{ x: 2 }}>
              <div style={S.iconBadge}><CreditCard size={18} color={F.red} /></div>
              <div style={{ flex: 1 }}>
                <div style={S.rowTitle}>{card.brand?.toUpperCase()} {maskLast4(card.last4)}</div>
                <div style={S.rowSub}>Expires {card.expMonth}/{card.expYear} · {card.country}</div>
              </div>
            </motion.div>
          ))
        ) : (
          <EmptyRow text="No cards saved yet." />
        )}
      </div>

      <AnimatePresence initial={false}>
        {!expanded && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={S.addRow} onClick={() => setExpanded(true)}>
            <PlusCircle size={18} color={F.red} />
            <span style={{ marginLeft: 10, fontWeight: 800, color: F.ink, fontSize: 13.5 }}>Add a payment method</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            style={{ overflow: "hidden" }}>
            <FormShell title="Card details" onClose={() => setExpanded(false)}>
              <Field label="Card number" name="cardNumber" value={form.cardNumber} placeholder="1234 5678 9012 3456" onChange={onChange} />
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}><Field label="MM" name="expMonth" value={form.expMonth} placeholder="MM" onChange={onChange} /></div>
                <div style={{ flex: 1 }}><Field label="YY" name="expYear" value={form.expYear} placeholder="YY" onChange={onChange} /></div>
                <div style={{ flex: 1 }}><Field label="CVV" name="cvv" value={form.cvv} placeholder="•••" onChange={onChange} /></div>
              </div>
              <Field label="ZIP / postal code" name="zip" value={form.zip} placeholder="12345" onChange={onChange} />
              <Field label="Country" name="country" value={form.country} onChange={onChange} as="select">
                <option value="">Select country…</option>
                {countries.map((c: any) => <option key={c.code} value={c.code}>{c.emoji} {c.name}</option>)}
              </Field>
              <button className="wl-btn" style={S.primaryBtn} onClick={handleSubmit}>Save card</button>
            </FormShell>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PAYOUTS — balance, saved payout methods, and add/edit forms
   for how the host/seller receives money.
═══════════════════════════════════════════════════════════ */
type MethodTabKey = "directBankTransfer" | "mpesaTransfer" | "paypal" | "wise" | "skrill" | "stripe";

// Comment out any line here to remove that payout option from the UI.
// Each one maps 1:1 to a BACKEND entry above.
const PAYOUT_TABS: { key: MethodTabKey; label: string; icon: React.ReactElement }[] = [
  { key: "directBankTransfer", label: "Bank transfer", icon: <Landmark size={12} /> },
  { key: "mpesaTransfer", label: "M-Pesa", icon: <Smartphone size={12} /> },
  { key: "paypal", label: "PayPal", icon: <Wallet2 size={12} /> }, //{paypal}
  { key: "wise", label: "Wise", icon: <Landmark size={12} /> }, //{wise}
  { key: "skrill", label: "Skrill", icon: <Mail size={12} /> }, //{skrill}
  { key: "stripe", label: "Stripe", icon: <CreditCard size={12} /> }, //{stripe}
];

const PayoutsPanel: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = (path: string) => { window.location.href = path; }; // swap for useNavigate() if this file sits under a Router

  const { pendingBalance, loading: balanceLoading } = useSelector((s: RootState) => s.payouts);
  const { payoutMethods, loading: methodsLoading } = useSelector((s: RootState) => s.payoutMethod);
  const [hidden, setHidden] = useState(false);
  const [methodTab, setMethodTab] = useState<MethodTabKey>(PAYOUT_TABS[0].key);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToHostPayout(userId));
    dispatch(fetchPayoutMethodsList({ userId }));
  }, [userId, dispatch]);

  const maskedValue = useMemo(() => {
    const digits = (pendingBalance ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 }).replace(/[^0-9]/g, "");
    return "*".repeat(digits.length);
  }, [pendingBalance]);

  const noMethods = !Object.keys(payoutMethods || {}).length;

  const renderIcon = (method: any) => {
    const type = method?.payoutMethod || "";
    if (type.includes("wise")) return <Landmark size={18} color={F.red} />;
    if (type === "skrill") return <Mail size={18} color={F.red} />;
    if (type === "paypal") return <Wallet2 size={18} color={F.red} />;
    if (type === "stripe") return <CreditCard size={18} color={F.red} />;
    return <Landmark size={18} color={F.red} />;
  };

  if (balanceLoading) {
    return <div style={S.loaderBox}><CircularProgress sx={{ color: F.red }} /></div>;
  }

  return (
    <div className="wl-rise">
      {/* ── balance hero ── */}
      <div style={S.balanceCard}>
        <div style={S.balanceTop}>
          <div style={S.eyebrowLight}>TOTAL WEALTH</div>
          <div style={S.eyeBtn} onClick={() => setHidden((h) => !h)}>
            {hidden ? <EyeOff size={18} color="#fff" /> : <Eye size={18} color="#fff" />}
          </div>
        </div>
        <div style={S.bigNumber}>
          € {hidden ? maskedValue : (pendingBalance ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </div>
        <div style={S.iconRow}>
          <div style={S.iconChip} onClick={() => navigate(ROUTES.walletAnalytics)}>
            <BarChart3 size={20} color="#fff" /><span>Analytics</span>
          </div>
          <div style={S.iconChip} onClick={() => navigate(ROUTES.walletTransactions)}>
            <History size={20} color="#fff" /><span>Transactions</span>
          </div>
        </div>
      </div>

      {/* ── saved methods ── */}
      <PanelHeading eyebrow="Receiving money" title="Payout methods" />

      {methodsLoading ? (
        <div style={S.loaderBox}><CircularProgress size={26} sx={{ color: F.red }} /></div>
      ) : noMethods ? (
        <EmptyRow text="No payout methods yet — add one below." />
      ) : (
        <div style={S.stack}>
          {Object.entries(payoutMethods as Record<string, any>).map(([key, method]) => (
            <motion.div key={key} layout className="wl-card wl-clickable" style={S.rowCard} onClick={() => setExpanded(expanded === key ? null : key)}
              whileHover={{ x: 2 }}>
              <div style={S.iconBadge}>{renderIcon(method)}</div>
              <div style={{ flex: 1 }}>
                <div style={S.rowTitle}>{titleCase(method?.payoutMethod)}</div>
                <div style={S.rowSub}>{method.currency} · {method.legalType}</div>
              </div>
              <ChevronRight size={16} color={F.mutedDim} style={{ transform: expanded === key ? "rotate(90deg)" : "none", transition: "transform .2s" }} />
              <AnimatePresence>
                {expanded === key && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    style={{ width: "100%", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
                    <div style={S.detailBox}>
                      <DetailRow label="Full name" value={method.fullName} />
                      {"iban" in method && <DetailRow label="IBAN" value={maskLast4(method.iban)} />}
                      {"accountNumber" in method && <DetailRow label="Account number" value={maskLast4(method.accountNumber)} />}
                      {"skrillEmail" in method && <DetailRow label="Skrill email" value={method.skrillEmail} />}
                      {"paypalEmail" in method && <DetailRow label="PayPal email" value={method.paypalEmail} />}
                      {"address" in method && <DetailRow label="Country" value={method.address?.country} />}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      {/* ── add / edit method ── */}
      <div style={{ marginTop: 26 }}>
        <div style={S.eyebrow}>ADD OR UPDATE A METHOD</div>
        <div style={{ display: "flex", gap: 8, margin: "10px 0 18px", flexWrap: "wrap" }}>
          {PAYOUT_TABS.map((t) => (
            <button key={t.key} className={`wl-pill${methodTab === t.key ? " active" : ""}`} onClick={() => setMethodTab(t.key)}>
              {React.cloneElement(t.icon as React.ReactElement<any>, { style: { verticalAlign: -2, marginRight: 5 } })}{t.label}
            </button>
          ))}
        </div>

        {methodTab === "directBankTransfer" && <BankTransferForm userId={userId} />}
        {methodTab === "mpesaTransfer" && <MpesaForm userId={userId} />}
        {methodTab === "paypal" && <PaypalPayoutForm userId={userId} />}
        {methodTab === "wise" && <WisePayoutForm userId={userId} />}
        {methodTab === "skrill" && <SkrillPayoutForm userId={userId} />}
        {methodTab === "stripe" && <StripeConnectPanel userId={userId} />}
      </div>
    </div>
  );
};

/* ── Bank transfer form (IBAN OR account+routing, same rules as source) ── */
const BankTransferForm: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const payoutMethods = useSelector((s: RootState) => s.payoutDetails.methods);
  const defaultForm = {
    type: "directBankTransfer" as const, fullName: "", currency: "", iban: "",
    accountNumber: "", routingNumber: "", accountType: "CHECKING",
    address: { country: "", state: "", city: "", firstLine: "", postCode: "" },
  };
  const [form, setForm] = useState(defaultForm);
  const [initialForm, setInitialForm] = useState(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState("");

  const isIBANUsed = !!form.iban.trim();

  const validate = (name: string, value: string): string => {
    switch (name) {
      case "fullName": return /^[A-Za-z\s]+$/.test(value) ? "" : "Only letters allowed";
      case "accountNumber": return isIBANUsed || /^\d{5,17}$/.test(value) ? "" : "5–17 digits required";
      case "routingNumber": return isIBANUsed || /^\d{9}$/.test(value) ? "" : "Must be exactly 9 digits";
      case "iban": return !value || /^[A-Z0-9]{15,34}$/.test(value) ? "" : "Invalid IBAN";
      case "currency": case "country": case "state": case "city": case "firstLine": case "postCode":
        return value.trim() ? "" : "Required";
      default: return "";
    }
  };

  const onChange = (name: string, raw: string) => {
    const value = name === "iban" ? raw.toUpperCase()
      : name === "accountNumber" || name === "routingNumber" ? raw.replace(/\D/g, "") : raw;
    if (name in form.address) setForm((p) => ({ ...p, address: { ...p.address, [name]: value } }));
    else setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: validate(name, value) }));
  };

  const isValid = Object.values(errors).every((e) => !e) && form.fullName && form.currency &&
    form.address.country && form.address.state && form.address.city && form.address.firstLine && form.address.postCode &&
    (isIBANUsed ? /^[A-Z0-9]{15,34}$/.test(form.iban) : /^\d{5,17}$/.test(form.accountNumber) && /^\d{9}$/.test(form.routingNumber));
  const isChanged = JSON.stringify(form) !== JSON.stringify(initialForm);

  useEffect(() => { if (userId) dispatch(fetchPayoutDetails(userId)); }, [userId, dispatch]);
  useEffect(() => {
    const existing: any = payoutMethods?.find((m: any) => m.type === "directBankTransfer");
    if (existing) {
      const safe = { ...defaultForm, ...existing } as typeof defaultForm;
      setForm(safe); setInitialForm(safe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutMethods]);

  const handleSubmit = async () => {
    if (!userId || !isValid) return;
    setLoading(true);
    setFormError("");
    try {
      await dispatch(upsertPayoutMethod({ userId, method: form })).unwrap();
      setInitialForm(form); setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || "Something went wrong saving your bank details.");
    } finally { setLoading(false); }
  };

  return (
    <FormShell title="Direct bank transfer" trailing={<Seal show={saved} />}>
      <Field label="Full name" name="fullName" value={form.fullName} error={errors.fullName} onChange={onChange} placeholder="As it appears on the account" />
      <Field label="Currency" name="currency" value={form.currency} error={errors.currency} onChange={onChange} placeholder="EUR, USD…" />
      <Field label="IBAN (optional)" name="iban" value={form.iban} error={errors.iban} onChange={onChange} placeholder="Leave blank to use account + routing instead" />
      {!isIBANUsed && (
        <>
          <Field label="Account number" name="accountNumber" value={form.accountNumber} error={errors.accountNumber} onChange={onChange} />
          <Field label="Routing number" name="routingNumber" value={form.routingNumber} error={errors.routingNumber} onChange={onChange} />
        </>
      )}
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}><Field label="Country" name="country" value={form.address.country} error={errors.country} onChange={onChange} /></div>
        <div style={{ flex: 1 }}><Field label="State" name="state" value={form.address.state} error={errors.state} onChange={onChange} /></div>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}><Field label="City" name="city" value={form.address.city} error={errors.city} onChange={onChange} /></div>
        <div style={{ flex: 1 }}><Field label="Postal code" name="postCode" value={form.address.postCode} error={errors.postCode} onChange={onChange} /></div>
      </div>
      <Field label="Street address" name="firstLine" value={form.address.firstLine} error={errors.firstLine} onChange={onChange} />
      {formError && <div className="wl-errtext" style={{ marginBottom: 12 }}>{formError}</div>}
      <button className="wl-btn" style={S.primaryBtn} disabled={!isValid || loading || !isChanged} onClick={handleSubmit}>
        {loading ? "Saving…" : !isChanged && saved ? "Saved" : "Save bank details"}
      </button>
    </FormShell>
  );
};

/* ── M-Pesa form (phone OR paybill+account OR till — mutually exclusive) ── */
const MpesaForm: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const payoutMethods = useSelector((s: RootState) => s.payoutDetails.methods);
  const defaultForm = { type: "mpesaTransfer" as const, phoneNumber: "", paybillNumber: "", accountNumber: "", tillNumber: "" };
  const [form, setForm] = useState(defaultForm);
  const [initialForm, setInitialForm] = useState(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState("");

  const onChange = (name: string, raw: string) => {
    const value = name === "phoneNumber" ? raw.replace(/\D/g, "") : raw;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: name === "phoneNumber" && value && !/^\d{10,15}$/.test(value) ? "Valid phone required" : "" }));
  };

  const isValid = Object.values(errors).every((e) => !e) &&
    (form.phoneNumber || (form.paybillNumber && form.accountNumber) || form.tillNumber);
  const isChanged = JSON.stringify(form) !== JSON.stringify(initialForm);

  const phoneDisabled = !!form.paybillNumber || !!form.tillNumber;
  const paybillDisabled = !!form.phoneNumber || !!form.tillNumber;
  const tillDisabled = !!form.phoneNumber || !!form.paybillNumber;

  useEffect(() => { if (userId) dispatch(fetchPayoutDetails(userId)); }, [userId, dispatch]);
  useEffect(() => {
    const existing: any = payoutMethods?.find((m: any) => m.type === "mpesaTransfer");
    if (existing) { const safe = { ...defaultForm, ...existing } as typeof defaultForm; setForm(safe); setInitialForm(safe); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutMethods]);

  const handleSubmit = async () => {
    if (!userId || !isValid) return;
    setLoading(true);
    setFormError("");
    try {
      await dispatch(upsertPayoutMethod({ userId, method: form })).unwrap();
      setInitialForm(form); setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || "Something went wrong saving your M-Pesa details.");
    } finally { setLoading(false); }
  };

  return (
    <FormShell title="M-Pesa" trailing={<Seal show={saved} />}>
      <Field label="Phone number" name="phoneNumber" value={form.phoneNumber} error={errors.phoneNumber} onChange={onChange} disabled={phoneDisabled} placeholder="07XXXXXXXX" />
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}><Field label="Paybill number" name="paybillNumber" value={form.paybillNumber} onChange={onChange} disabled={paybillDisabled} /></div>
        <div style={{ flex: 1 }}><Field label="Account number" name="accountNumber" value={form.accountNumber} onChange={onChange} disabled={paybillDisabled} /></div>
      </div>
      <Field label="Till number" name="tillNumber" value={form.tillNumber} onChange={onChange} disabled={tillDisabled} />
      {formError && <div className="wl-errtext" style={{ marginBottom: 12 }}>{formError}</div>}
      <button className="wl-btn" style={S.primaryBtn} disabled={!isValid || loading || !isChanged} onClick={handleSubmit}>
        {loading ? "Saving…" : !isChanged && saved ? "Saved" : "Save M-Pesa details"}
      </button>
    </FormShell>
  );
};

/* ── PayPal payout form ── */
const PaypalPayoutForm: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const payoutMethods = useSelector((s: RootState) => s.payoutDetails.methods);
  const defaultForm = { type: "paypal" as const, email: "", currency: "" };
  const [form, setForm] = useState(defaultForm);
  const [initialForm, setInitialForm] = useState(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState("");

  const onChange = (name: string, value: string) => {
    setForm((p) => ({ ...p, [name]: value }));
    const err = name === "email" ? (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Invalid email format")
      : name === "currency" ? (value.trim() ? "" : "Currency required") : "";
    setErrors((p) => ({ ...p, [name]: err }));
  };

  const isValid = Object.values(errors).every((e) => !e) && form.email && form.currency;
  const isChanged = JSON.stringify(form) !== JSON.stringify(initialForm);

  useEffect(() => { if (userId) dispatch(fetchPayoutDetails(userId)); }, [userId, dispatch]);
  useEffect(() => {
    const existing: any = payoutMethods?.find((m: any) => m.type === "paypal");
    if (existing) { const safe = { ...defaultForm, ...existing } as typeof defaultForm; setForm(safe); setInitialForm(safe); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutMethods]);

  const handleSubmit = async () => {
    if (!userId || !isValid) return;
    setLoading(true);
    setFormError("");
    try {
      // { swap in BACKEND.paypalPayoutBackend above if/when a dedicated function exists }
      await dispatch(upsertPayoutMethod({ userId, method: form })).unwrap();
      setInitialForm(form); setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || "Something went wrong saving your PayPal details.");
    } finally { setLoading(false); }
  };

  return (
    <FormShell title="PayPal" trailing={<Seal show={saved} />}>
      <Field label="PayPal email" name="email" value={form.email} error={errors.email} onChange={onChange} placeholder="you@example.com" />
      <Field label="Currency" name="currency" value={form.currency} error={errors.currency} onChange={onChange} placeholder="EUR, USD…" />
      {formError && <div className="wl-errtext" style={{ marginBottom: 12 }}>{formError}</div>}
      <button className="wl-btn" style={S.primaryBtn} disabled={!isValid || loading || !isChanged} onClick={handleSubmit}>
        {loading ? "Saving…" : !isChanged && saved ? "Saved" : "Save PayPal details"}
      </button>
    </FormShell>
  );
};

/* ── Wise payout form: EUR → IBAN and USD → SWIFT, side by side ── */
type Address = { country: string; city: string; firstLine: string; postCode: string };
const initialAddress: Address = { country: "", city: "", firstLine: "", postCode: "" };

interface EurForm { fullName: string; legalType: string; IBAN: string; BIC: string; address: Address; }
interface SwiftForm { fullName: string; legalType: string; swiftCode: string; accountNumber: string; address: Address; }

const clean = (input: string) => input.replace(/[\s-]/g, "");
const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,}$/;
const ibanRegex = /^[A-Z0-9]{15,34}$/;
const swiftRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
const acctRegex = /^[0-9]{6,34}$/;

const emptyEurForm: EurForm = { fullName: "", legalType: "", IBAN: "", BIC: "", address: initialAddress };
const emptySwiftForm: SwiftForm = { fullName: "", legalType: "", swiftCode: "", accountNumber: "", address: initialAddress };

const WisePayoutForm: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const payoutMethods = useSelector((s: RootState) => s.payoutDetails.methods);

  const [eurForm, setEurForm] = useState<EurForm>(emptyEurForm);
  const [eurInitialForm, setEurInitialForm] = useState<EurForm>(emptyEurForm);
  const [eurErrors, setEurErrors] = useState<Record<string, string>>({});
  const [eurFormError, setEurFormError] = useState("");
  const [eurLoading, setEurLoading] = useState(false);
  const [eurSuccess, setEurSuccess] = useState(false);

  const [swiftForm, setSwiftForm] = useState<SwiftForm>(emptySwiftForm);
  const [swiftInitialForm, setSwiftInitialForm] = useState<SwiftForm>(emptySwiftForm);
  const [swiftErrors, setSwiftErrors] = useState<Record<string, string>>({});
  const [swiftFormError, setSwiftFormError] = useState("");
  const [swiftLoading, setSwiftLoading] = useState(false);
  const [swiftSuccess, setSwiftSuccess] = useState(false);

  const eurChanged = JSON.stringify(eurForm) !== JSON.stringify(eurInitialForm);
  const swiftChanged = JSON.stringify(swiftForm) !== JSON.stringify(swiftInitialForm);

  useEffect(() => { if (userId) dispatch(fetchPayoutDetails(userId)); }, [userId, dispatch]);
  useEffect(() => {
    const existingEur: any = payoutMethods?.find((m: any) => m.payoutMethod === "wise_eur_iban");
    if (existingEur) {
      const safe = { ...emptyEurForm, ...existingEur } as EurForm;
      setEurForm(safe); setEurInitialForm(safe);
    }
    const existingSwift: any = payoutMethods?.find((m: any) => m.payoutMethod === "wise_usd_swift");
    if (existingSwift) {
      const safe = { ...emptySwiftForm, ...existingSwift } as SwiftForm;
      setSwiftForm(safe); setSwiftInitialForm(safe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutMethods]);

  const validateEur = (form: EurForm) => {
    const e: Record<string, string> = {};
    if (!nameRegex.test(form.fullName.trim())) e.fullName = "Invalid name";
    if (!form.legalType) e.legalType = "Required";
    if (!ibanRegex.test(clean(form.IBAN).toUpperCase())) e.IBAN = "Bad IBAN";
    if (form.BIC && !swiftRegex.test(clean(form.BIC).toUpperCase())) e.BIC = "Bad BIC";
    if (!form.address.country || !form.address.city || !form.address.firstLine || !form.address.postCode) e.address = "Complete address required";
    return e;
  };
  const validateSwift = (form: SwiftForm) => {
    const e: Record<string, string> = {};
    if (!nameRegex.test(form.fullName.trim())) e.fullName = "Invalid name";
    if (!form.legalType) e.legalType = "Required";
    if (!swiftRegex.test(form.swiftCode.trim().toUpperCase())) e.swiftCode = "Bad SWIFT";
    if (!acctRegex.test(form.accountNumber.trim())) e.accountNumber = "Bad account #";
    if (!form.address.country || !form.address.city || !form.address.firstLine || !form.address.postCode) e.address = "Complete address required";
    return e;
  };

  const onEurChange = (name: string, value: string) => {
    const next = name in eurForm.address ? { ...eurForm, address: { ...eurForm.address, [name]: value } } : { ...eurForm, [name]: value };
    setEurForm(next as EurForm);
    setEurErrors(validateEur(next as EurForm));
  };
  const onSwiftChange = (name: string, value: string) => {
    const next = name in swiftForm.address ? { ...swiftForm, address: { ...swiftForm.address, [name]: value } } : { ...swiftForm, [name]: value };
    setSwiftForm(next as SwiftForm);
    setSwiftErrors(validateSwift(next as SwiftForm));
  };

  const submitEur = async () => {
    const errs = validateEur(eurForm);
    setEurErrors(errs);
    setEurFormError("");
    if (Object.keys(errs).length || !userId) return;
    setEurLoading(true);
    try {
      const payload = {
        payoutType: "EUR_IBAN", hostId: userId, currency: "EUR",
        fullName: eurForm.fullName.trim(), legalType: eurForm.legalType,
        iban: clean(eurForm.IBAN).toUpperCase(),
        bic: eurForm.BIC ? clean(eurForm.BIC).toUpperCase() : undefined,
        address: eurForm.address,
      };
      const res = await axios.post<{ recipientId: string }>(BACKEND.wiseRecipient, payload);
      if (!res.data?.recipientId) throw new Error("No recipientId returned from Wise.");
      await dispatch(upsertPayoutMethod({
        userId,
        method: { type: "wise", payoutMethod: "wise_eur_iban", ...payload } as any,
      })).unwrap();
      setEurInitialForm(eurForm);
      setEurSuccess(true);
      setTimeout(() => setEurSuccess(false), 2400);
    } catch (err: any) {
      setEurFormError(err?.response?.data?.message || err.message || "Something went wrong saving your EUR payout details.");
    } finally { setEurLoading(false); }
  };

  const submitSwift = async () => {
    const errs = validateSwift(swiftForm);
    setSwiftErrors(errs);
    setSwiftFormError("");
    if (Object.keys(errs).length || !userId) return;
    setSwiftLoading(true);
    try {
      const payload = {
        payoutType: "USD_SWIFT", hostId: userId, currency: "USD",
        fullName: swiftForm.fullName.trim(), legalType: swiftForm.legalType,
        swiftCode: swiftForm.swiftCode.trim().toUpperCase(),
        accountNumber: swiftForm.accountNumber.trim(),
        address: swiftForm.address,
      };
      const res = await axios.post<{ recipientId: string }>(BACKEND.wiseRecipient, payload);
      if (!res.data?.recipientId) throw new Error("No recipientId returned from Wise.");
      await dispatch(upsertPayoutMethod({
        userId,
        method: { type: "wise", payoutMethod: "wise_usd_swift", ...payload } as any,
      })).unwrap();
      setSwiftInitialForm(swiftForm);
      setSwiftSuccess(true);
      setTimeout(() => setSwiftSuccess(false), 2400);
    } catch (err: any) {
      setSwiftFormError(err?.response?.data?.message || err.message || "Something went wrong saving your USD payout details.");
    } finally { setSwiftLoading(false); }
  };

  const renderAddressFields = (address: Address, errors: Record<string, string>, onChange: (n: string, v: string) => void) => (
    <>
      <Field label="Country" name="country" value={address.country} onChange={onChange} as="select" error={errors.address}>
        <option value="">Select country…</option>
        {countries.map((c: any) => <option key={c.code} value={c.code}>{c.emoji} {c.name} ({c.code})</option>)}
      </Field>
      <Field label="City" name="city" value={address.city} onChange={onChange} error={errors.address} />
      <Field label="Street address" name="firstLine" value={address.firstLine} onChange={onChange} error={errors.address} />
      <Field label="Post code" name="postCode" value={address.postCode} onChange={onChange} error={errors.address} />
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <FormShell title="Wise · EUR via IBAN (European residents)" trailing={<Seal show={eurSuccess} />}>
        <Field label="Full name" name="fullName" value={eurForm.fullName} error={eurErrors.fullName} onChange={onEurChange} />
        <Field label="Recipient type" name="legalType" value={eurForm.legalType} onChange={onEurChange} as="select" error={eurErrors.legalType}>
          <option value="">Select…</option>
          <option value="PRIVATE">Person</option>
          <option value="BUSINESS">Business</option>
        </Field>
        <Field label="IBAN" name="IBAN" value={eurForm.IBAN} error={eurErrors.IBAN} onChange={onEurChange} />
        <Field label="BIC / SWIFT (optional)" name="BIC" value={eurForm.BIC} error={eurErrors.BIC} onChange={onEurChange} />
        {renderAddressFields(eurForm.address, eurErrors, onEurChange)}
        {eurFormError && <div className="wl-errtext" style={{ marginBottom: 12 }}>{eurFormError}</div>}
        <button className="wl-btn" style={S.primaryBtn} disabled={eurLoading || !eurChanged} onClick={submitEur}>
          {eurLoading ? "Sending…" : !eurChanged && eurInitialForm.IBAN ? "Saved" : "Save EUR payout details"}
        </button>
      </FormShell>

      <FormShell title="Wise · USD via SWIFT (international residents)" trailing={<Seal show={swiftSuccess} />}>
        <Field label="Full name" name="fullName" value={swiftForm.fullName} error={swiftErrors.fullName} onChange={onSwiftChange} />
        <Field label="Recipient type" name="legalType" value={swiftForm.legalType} onChange={onSwiftChange} as="select" error={swiftErrors.legalType}>
          <option value="">Select…</option>
          <option value="PRIVATE">Person</option>
          <option value="BUSINESS">Business</option>
        </Field>
        <Field label="SWIFT / BIC code" name="swiftCode" value={swiftForm.swiftCode} error={swiftErrors.swiftCode} onChange={onSwiftChange} />
        <Field label="Account number" name="accountNumber" value={swiftForm.accountNumber} error={swiftErrors.accountNumber} onChange={onSwiftChange} />
        {renderAddressFields(swiftForm.address, swiftErrors, onSwiftChange)}
        {swiftFormError && <div className="wl-errtext" style={{ marginBottom: 12 }}>{swiftFormError}</div>}
        <button className="wl-btn" style={S.primaryBtn} disabled={swiftLoading || !swiftChanged} onClick={submitSwift}>
          {swiftLoading ? "Sending…" : !swiftChanged && swiftInitialForm.swiftCode ? "Saved" : "Save USD payout details"}
        </button>
      </FormShell>
    </div>
  );
};

/* ── Skrill payout form ── */
const emptySkrillForm = { email: "", currency: "" };

const SkrillPayoutForm: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const payoutMethods = useSelector((s: RootState) => s.payoutDetails.methods);
  const [form, setForm] = useState(emptySkrillForm);
  const [initialForm, setInitialForm] = useState(emptySkrillForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const onChange = (name: string, value: string) => {
    setForm((p) => ({ ...p, [name]: value }));
    const err = name === "email" ? (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Invalid email format")
      : name === "currency" ? (value.trim() ? "" : "Currency required") : "";
    setErrors((p) => ({ ...p, [name]: err }));
  };

  const isValid = Object.values(errors).every((e) => !e) && form.email && form.currency;
  const isChanged = JSON.stringify(form) !== JSON.stringify(initialForm);

  useEffect(() => { if (userId) dispatch(fetchPayoutDetails(userId)); }, [userId, dispatch]);
  useEffect(() => {
    const existing: any = payoutMethods?.find((m: any) => m.payoutMethod === "skrill");
    if (existing) {
      const safe = { email: existing.skrillEmail || "", currency: existing.currency || "" };
      setForm(safe); setInitialForm(safe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutMethods]);

  const handleSubmit = async () => {
    if (!userId || !isValid) return;
    setLoading(true);
    setFormError("");
    try {
      // NOTE: point BACKEND.skrillRecipient at your real Skrill payout function once it exists.
      await axios.post(BACKEND.skrillRecipient, { hostId: userId, skrillEmail: form.email, currency: form.currency });
      await dispatch(upsertPayoutMethod({
        userId,
        method: { type: "skrill", payoutMethod: "skrill", currency: form.currency, skrillEmail: form.email } as any,
      })).unwrap();
      setInitialForm(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err.message || "Something went wrong saving your Skrill details.");
    } finally { setLoading(false); }
  };

  return (
    <FormShell title="Skrill" trailing={<Seal show={saved} />}>
      <Field label="Skrill email" name="email" value={form.email} error={errors.email} onChange={onChange} placeholder="you@example.com" />
      <Field label="Currency" name="currency" value={form.currency} error={errors.currency} onChange={onChange} placeholder="EUR, USD…" />
      {formError && <div className="wl-errtext" style={{ marginBottom: 12 }}>{formError}</div>}
      <button className="wl-btn" style={S.primaryBtn} disabled={!isValid || loading || !isChanged} onClick={handleSubmit}>
        {loading ? "Saving…" : !isChanged && saved ? "Saved" : "Save Skrill details"}
      </button>
    </FormShell>
  );
};

/* ── Stripe Connect onboarding panel ── */
const StripeConnectPanel: React.FC<{ userId?: string }> = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleConnect = async () => {
    if (!userId) { setError("You need to be logged in first."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post<{ url?: string }>(BACKEND.stripeConnect, { hostId: userId });
      const url = res.data?.url;
      if (!url) throw new Error("Failed to get Stripe onboarding URL.");
      window.location.href = url;
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Something went wrong connecting to Stripe.");
      setLoading(false);
    }
  };

  return (
    <FormShell title="Stripe">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={S.iconBadge}><CreditCard size={18} color={F.red} /></div>
        <p style={{ margin: 0, fontSize: 13, color: F.muted, lineHeight: 1.5 }}>
          Connect your bank account securely through Stripe to receive payouts. This is safe, fast, and only takes a minute.
        </p>
      </div>
      {error && <div className="wl-errtext" style={{ marginBottom: 12 }}>{error}</div>}
      <button className="wl-btn" style={S.primaryBtn} disabled={loading} onClick={handleConnect}>
        {loading ? "Connecting…" : "Connect payouts with Stripe"}
      </button>
    </FormShell>
  );
};

/* ═══════════════════════════════════════════════════════════
   REWARDS — award prize, credits summary, referrals
═══════════════════════════════════════════════════════════ */
const RewardsPanel: React.FC<{ userId?: string }> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.auth.user);
  const {
    awardPrize, awardsRunnersUp, credits, todayBalance, pendingBalance, paidOut, referrals, loading,
  } = useSelector((s: RootState) => s.payouts);

  const [expanded, setExpanded] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => { if (userId) dispatch(listenToHostPayout(userId)); }, [userId, dispatch]);

  const handleApply = () => {
    // { wire this up to your prize-application endpoint }
    setApplied(true);
    setTimeout(() => setApplied(false), 2400);
  };

  if (loading) return <div style={S.loaderBox}><CircularProgress sx={{ color: F.red }} /></div>;

  const metrics = [
    { label: "Available credits", value: credits, icon: <Coins size={18} color={F.red} /> },
    { label: "Today's balance", value: todayBalance, icon: <Activity size={18} color={F.red} /> },
    { label: "Pending balance", value: pendingBalance, icon: <Activity size={18} color={F.red} /> },
    { label: "Total paid out", value: paidOut, icon: <Coins size={18} color={F.red} /> },
  ];

  return (
    <div className="wl-rise">
      <PanelHeading eyebrow="Prize" title="Awards" action={<Seal show={applied} label="Application sent" />} />

      <motion.div className="wl-card" whileHover={{ x: 2 }} style={S.rowCard}>
        <div style={S.iconBadge}><Award size={18} color={F.red} /></div>
        <div style={{ flex: 1 }}>
          <div className="wl-amount" style={{ fontSize: 19 }}>€{formatCompact(awardPrize || 0)}</div>
          <div style={S.rowSub}>Award prize</div>
        </div>
      </motion.div>

      <motion.div className="wl-card wl-clickable" whileHover={{ x: 2 }} style={S.rowCard} onClick={() => setExpanded((e) => !e)}>
        <div style={S.iconBadge}><Gift size={18} color={F.red} /></div>
        <div style={{ flex: 1 }}>
          <div style={S.rowTitle}>Apply for the prize</div>
          <div style={S.rowSub}>Tap to preview your details and apply</div>
        </div>
        <ChevronRight size={16} color={F.mutedDim} style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform .2s" }} />
      </motion.div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ overflow: "hidden" }}>
            <FormShell title="Confirm your details">
              <Field label="Full name" name="fullName" value={`${user?.firstName || ""} ${user?.lastName || ""}`} onChange={() => {}} disabled />
              <Field label="Email" name="email" value={user?.emailAddress || user?.email || ""} onChange={() => {}} disabled />
              <Field label="Phone" name="phone" value={user?.phoneNumber || ""} onChange={() => {}} disabled />
              <button className="wl-btn" style={{ ...S.primaryBtn, opacity: awardsRunnersUp ? 1 : 0.5 }}
                disabled={!awardsRunnersUp} onClick={handleApply}>
                {awardsRunnersUp ? "Apply now" : "Runner-up status required"}
              </button>
            </FormShell>
          </motion.div>
        )}
      </AnimatePresence>

      <PanelHeading eyebrow="Ledger" title="Credits summary" style={{ marginTop: 30 }} />
      <div style={S.metricGrid}>
        {metrics.map((m, i) => (
          <motion.div key={i} className="wl-card" whileHover={{ y: -3 }} style={S.metricCard}>
            <div style={S.iconBadge}>{m.icon}</div>
            <div>
              <div className="wl-amount" style={{ fontSize: 18 }}>€{formatCompact(m.value || 0)}</div>
              <div style={S.rowSub}>{m.label}</div>
            </div>
          </motion.div>
        ))}
      </div>

      <PanelHeading eyebrow="Network" title="Referrals" style={{ marginTop: 30 }} />
      <motion.div className="wl-card" whileHover={{ x: 2 }} style={S.rowCard}>
        <div style={S.iconBadge}><Users size={18} color={F.red} /></div>
        <div style={{ flex: 1 }}>
          <div className="wl-amount" style={{ fontSize: 19 }}>€{formatCompact(referrals || 0)}</div>
          <div style={S.rowSub}>Total referral earnings</div>
        </div>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   shared bits: headings, empty state, form shell, detail row
═══════════════════════════════════════════════════════════ */
const PanelHeading: React.FC<{ eyebrow: string; title: string; action?: React.ReactNode; style?: React.CSSProperties }> = ({ eyebrow, title, action, style }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, ...style }}>
    <div>
      <div style={S.eyebrow}>{eyebrow}</div>
      <h2 style={S.panelTitle}>{title}</h2>
    </div>
    {action}
  </div>
);

const EmptyRow: React.FC<{ text: string }> = ({ text }) => (
  <div style={S.emptyRow}>{text}</div>
);

const DetailRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div style={S.detailLine}>
    <span style={{ color: F.mutedDim }}>{label}</span>
    <span style={{ color: F.ink, fontWeight: 700 }}>{value || "—"}</span>
  </div>
);

const FormShell: React.FC<{ title: string; onClose?: () => void; trailing?: React.ReactNode; children: React.ReactNode }> = ({ title, onClose, trailing, children }) => (
  <div style={S.formShell}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
      <span style={S.formTitle}>{title}</span>
      {trailing}
      {onClose && <X size={18} color={F.muted} style={{ cursor: "pointer" }} onClick={onClose} />}
    </div>
    {children}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", width: "100%", background: F.pageBg, color: F.ink },

  fixedHeader: {
    position: "fixed", top: 0, left: 0, right: 0, height: HEADER_H, zIndex: 20,
    background: F.headerBg, backgroundImage: F.grad, display: "flex", alignItems: "center",
    padding: "0 24px", boxShadow: "0 6px 24px rgba(255,61,87,.22)",
  },
  brandRow: { display: "flex", alignItems: "center", gap: 14 },
  logoRing: { width: 40, height: 40, borderRadius: "50%", background: "#fff", padding: 3, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 14px rgba(0,0,0,.12)" },
  logoImg: { width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" },
  eyebrowLight: { fontSize: 9.5, letterSpacing: ".18em", color: "rgba(255,255,255,.8)", fontWeight: 800, textTransform: "uppercase", marginBottom: 2 },
  title: { fontFamily: "'Baloo 2',sans-serif", fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: "0.02em", color: "#fff" },

  sidebar: {
    position: "fixed", top: HEADER_H, left: 0, bottom: 0, width: SIDEBAR_W, zIndex: 15,
    background: F.cardBg, borderRight: F.outline, padding: "22px 16px", overflowY: "auto",
  },
  sidebarSubhead: { fontSize: 12, color: F.muted, lineHeight: 1.5, fontWeight: 500 },

  mobileSubhead: { color: F.muted, fontSize: 13, marginBottom: 20, lineHeight: 1.55 },

  eyebrow: { fontSize: 10.5, letterSpacing: ".16em", color: F.red, fontWeight: 800, textTransform: "uppercase", marginBottom: 4 },
  panelTitle: { fontFamily: "'Baloo 2',sans-serif", fontSize: 21, fontWeight: 700, margin: "2px 0 0", color: F.ink },

  content: { maxWidth: 880, margin: "0 auto", padding: `${HEADER_H + 24}px 24px 40px`, width: "100%", position: "relative", zIndex: 1 },

  footerBar: {
    position: "fixed", bottom: 0, left: 0, right: 0, height: FOOTER_H, zIndex: 20,
    background: "#fff", borderTop: F.outline, display: "flex", alignItems: "center",
    padding: "6px 8px", boxShadow: "0 -4px 16px rgba(0,0,0,.06)",
  },

  stack: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 },
  rowCard: {
    display: "flex", alignItems: "center", gap: 14, background: F.cardBg, border: F.outline,
    borderRadius: 18, padding: "14px 16px", flexWrap: "wrap",
  },
  iconBadge: { width: 40, height: 40, borderRadius: "50%", background: F.peachDim, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  rowTitle: { fontSize: 14, fontWeight: 800, color: F.ink },
  rowSub: { fontSize: 12, color: F.muted, marginTop: 2, fontWeight: 500 },
  emptyRow: { border: `1.5px dashed rgba(255,122,26,.28)`, borderRadius: 18, padding: "22px 16px", textAlign: "center", color: F.mutedDim, fontSize: 13, marginBottom: 16, background: "#fff" },

  addRow: { display: "flex", alignItems: "center", border: `1.5px dashed ${F.red}`, borderRadius: 18, padding: "14px 16px", cursor: "pointer", marginBottom: 6, background: "#fff" },

  formShell: { background: "#fff", border: F.outline, borderRadius: 20, padding: 20, marginTop: 4, marginBottom: 20, boxShadow: "0 2px 14px rgba(255,122,26,.08)" },
  formTitle: { fontWeight: 800, fontSize: 14, color: F.ink },
  primaryBtn: { background: F.gradH, color: "#fff", marginTop: 4, boxShadow: "0 6px 18px rgba(255,61,87,.28)" },

  balanceCard: { background: F.grad, borderRadius: 22, padding: "26px 22px", marginBottom: 28, textAlign: "center", boxShadow: "0 10px 30px rgba(255,61,87,.24)" },
  balanceTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  eyeBtn: { width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,.20)", border: "1px solid rgba(255,255,255,.32)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  bigNumber: { fontFamily: "'Montserrat',sans-serif", fontSize: 40, fontWeight: 900, marginTop: 10, color: "#fff" },
  iconRow: { display: "flex", justifyContent: "center", gap: 14, marginTop: 22 },
  iconChip: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 20px", borderRadius: 16, border: "1px solid rgba(255,255,255,.3)", background: "rgba(255,255,255,.14)", cursor: "pointer", fontSize: 12, color: "#fff", fontWeight: 700 },

  detailBox: { marginTop: 14, paddingTop: 12, borderTop: `1px dashed rgba(255,122,26,.24)`, display: "flex", flexDirection: "column", gap: 8, width: "100%" },
  detailLine: { display: "flex", justifyContent: "space-between", fontSize: 12.5 },

  metricGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 12 },
  metricCard: { display: "flex", alignItems: "center", gap: 12, background: F.cardBg, border: F.outline, borderRadius: 18, padding: "16px" },

  loaderBox: { display: "flex", alignItems: "center", justifyContent: "center", height: 200 },
};